import React, { useState, useEffect, useRef } from 'react';
import { useFileTranscodeStore, useTranscodeEntry, TranscodeStatus } from '../stores/fileTranscodeStore';
import { transcodeBadge } from '../utils/transcodeBadge';
import { uploadWithProgress } from '../utils/uploadWithProgress';
import { formatDateTime } from '../utils/datetime';

import {
  DEFAULT_DURATION_MIN, durationFromEnd, endFromDuration, humanizeMinutes, isRangeInvalid,
  isoToLocalInput, localInputPlusMinutes, minutesBetween, onStartChanged, snapOnStartBlur,
} from '../utils/eventDuration';

export interface MoFile {
 id?: string;
 title: string;
 description?: string;
 file_url: string;
 file_type?: string;
 file_size?: number;
 file_hash: string;
 created_at: string;
 campaign_id?: string;
 campaign_name?: string;
 thumbnail_path?: string;
 duration?: number | null;
 event_start_at?: string | null;  // ISO — 행사 시작 시각(선택). 있으면 플레이어 카운트다운 오버레이.
 expires_at?: string | null;       // ISO — 라이프타임 만료 시각(선택). 지나면 슬롯에서 제거. 기본=행사시작+1h.
 publish_at?: string | null;       // ISO — 홍보 시작(111, 만료의 거울). 미래면 그때까지 재생 스킵. null=즉시.
 event_label?: string | null;     // 짧은 동적 라벨(장소 등, 선택). 있으면 플레이어 오버레이.
 overlay_position?: number | null; // 오버레이 위치(1~5). 1=현재 스타일. 시스템 출력(날짜)·슬롯 라벨에도 쓰여 항상 노출(2026-09-17).
 transcode_status?: TranscodeStatus;
 transcode_progress?: number;
 transcode_eta_sec?: number | null;
 transcode_error?: string | null;
 /** 서버 판단(/mofile/:hash/info) — 이 사용자가 고칠 수 있는가. 목록에서 연 파일은 미설정(=소유). */
 can_edit?: boolean;
}

/** 오버레이 위치 라벨(1~5). 가이드 이미지 /static/overlay-pos/pos{n}.png 와 짝. */
const OVERLAY_POS_LABELS: Record<number, string> = { 1: '현재', 2: '좌상단', 3: '좌하단', 4: '우하단', 5: '우상단' };




type VariantEntry = {
 kind: 'main' | 'variant';
 resolution: string | null;
 rotation?: number;   // 0/90/270 — 세로 화면용 회전본(같은 해상도라도 별개 아티팩트)
 fps?: number | null; // 30/60 — fps 도 정체성 축(같은 해상도라도 별개 아티팩트)
 width: number;
 height: number;
 file_size: number;
 transcode_status: TranscodeStatus;
 transcode_progress: number | null;
 download_url: string;
};

// 회전본 라벨 — 세로 설치 화면용. "세로 ↻90°" / "세로 ↺90°"(=270). 0/미지정은 없음.
const rotationLabel = (rot?: number): string | null =>
 rot === 90 ? '세로 ↻90°' : rot === 270 ? '세로 ↺90°' : null;

// canonical fps(30/60). ⚠️ 임계값 59.5 는 서버 models/variant-key.ts canonicalFps 및
// migration 077 fps_key 백필과 반드시 동일 — 어긋나면 슬러그가 서버와 달라져 상태키/삭제/404.
const canonFps = (fps?: number | null): 30 | 60 => (fps != null && Number(fps) >= 59.5 ? 60 : 30);

// 회전+fps-접힌 variantKey 슬러그(상태 store 키·삭제 라우트용). 서버 variantKey 와 동일 포맷:
// r=0·fps=30 접미 생략, 접미 순서 _r 먼저 _f 나중. 예: "WxH", "WxH_r90", "WxH_f60", "WxH_r90_f60".
const variantSlug = (resolution: string, rot?: number, fps?: number | null): string => {
 const r = rot && rot !== 0 ? `_r${rot}` : '';
 const f = canonFps(fps) === 60 ? '_f60' : '';
 return `${resolution}${r}${f}`;
};

const formatBytes = (bytes: number): string => {
 if (bytes < 1024) return `${bytes} B`;
 if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
 if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
 return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const aspectIcon = (w: number, h: number): string => {
 if (w === 0 || h === 0) return '';
 return w >= h ? '🖥️' : '📱';
};

// 영상 키 첨부(격리 모듈 /attachments). 재생과 무관한 문서/파일.
type AttachmentItem = { id: number; filename: string; isPublic: boolean; byteSize: number; createdAt?: string };
const attIsVideo = (f: string): boolean => /\.(mp4|webm|mov|m4v)$/i.test(f);
const attVideoMime = (f: string): string =>
 /\.webm$/i.test(f) ? 'video/webm' : /\.mov$/i.test(f) ? 'video/quicktime' : /\.m4v$/i.test(f) ? 'video/x-m4v' : 'video/mp4';
const AttVideoThumb: React.FC<{ id: number }> = ({ id }) => {
 const [ok, setOk] = useState(true);
 if (!ok) return null;
 return <img src={`/attachments/attachment/${id}/thumbnail`} onError={() => setOk(false)} alt=""
  className="w-16 h-10 object-cover rounded bg-black shrink-0" />;
};

export const FileDetailModal: React.FC<{
 file: MoFile | null;
 onClose: () => void;
 onDelete: () => void;
 onUpdate: (fileHash: string, newTitle: string) => void;
 onAddToEmptySlot?: (fileHash: string) => Promise<{ ok: boolean; message: string }>;
 /**
  * 남의 파일을 볼 때 **변경 수단을 아예 감춘다**(기본 false = 종전 동작).
  *
  * 왜 필요한가: /mofile/:hash/info 는 "활성 슬롯에 참조된 파일이면 소유자가 아니어도
  * 메타데이터 허용" 이라, 스케줄 탭의 **조상 섹션**(남이 편성한 영상)에서도 이 모달이
  * 열린다. 실제 차단은 각 mutation 라우트가 uploaded_by 로 하지만(403), 눌러 보고
  * 실패 알림을 받는 흐름은 "되는 줄 알았다"를 만든다. 서버 판단(can_edit)을 받아
  * 처음부터 안 보여준다.
  */
 readOnly?: boolean;
}> = ({
 file,
 onClose,
 onDelete,
 onUpdate,
 onAddToEmptySlot,
 readOnly = false
}) => {
 // 계층도에서 열렸을 때만 주입되는 "빈 슬롯에 넣기"(모바일 드래그 대체) 상태.
 const [slotting, setSlotting] = useState(false);
 const [slotMsg, setSlotMsg] = useState<{ ok: boolean; text: string } | null>(null);
 const [videoDuration, setVideoDuration] = useState<number | null>(null);
 const [isEditingTitle, setIsEditingTitle] = useState(false);
 const [editTitle, setEditTitle] = useState('');
 const [editEventStart, setEditEventStart] = useState(''); // datetime-local (편집 중)
 const [editExpires, setEditExpires] = useState('');       // 만료 datetime-local (편집 중)
 const [editExpiresEnabled, setEditExpiresEnabled] = useState(false); // 만료 체크박스
 // 행사 시간(분, 문자열 — 타이핑 중 빈값 허용). 시작·종료와 **양방향**으로 물린다:
 // 행사 시간 입력 → 종료 = 시작 + 행사시간 / 종료 입력 → 행사시간 = 종료 - 시작.
 // 저장되는 값은 어디까지나 시작·종료(event_start_at·expires_at)이고 이건 입력 보조다.
 const [editDurationMin, setEditDurationMin] = useState('');
 const [isEditingEvent, setIsEditingEvent] = useState(false);
 // 홍보 시작(publish_at) — 만료의 거울. 독립 행으로 편집(행사·만료의 duration 연동과 무관).
 const [editPublish, setEditPublish] = useState('');
 const [isEditingPublish, setIsEditingPublish] = useState(false);
 // 제휴 송출(내 'Public' 박스 포함 여부) 토글 — BASIC 모바일에서 드래그 대신 체크박스.
 // 'none' = Public 박스 없음(제휴 미사용) → 행 자체를 감춘다. null = 로딩 중.
 const [pubShare, setPubShare] = useState<{ boxId: number; itemId: number | null; itemStart: number | null } | 'none' | null>(null);
 useEffect(() => {
  if (!file || readOnly) { setPubShare(null); return; }
  let alive = true;
  void (async () => {
   try {
    const r = await fetch('/videobox/mine', { credentials: 'include' });
    const j = await r.json();
    const pub = (j.data ?? []).find((b: any) => (b.name ?? '').trim() === 'Public');
    if (!pub) { if (alive) setPubShare('none'); return; }
    const d = await fetch(`/videobox/${pub.id}`, { credentials: 'include' }).then(x => x.json());
    const item = (d.data?.items ?? []).find((i: any) => i.fileHash === file.file_hash);
    if (alive) setPubShare({ boxId: pub.id, itemId: item?.id ?? null, itemStart: item?.interval?.start ?? null });
   } catch { if (alive) setPubShare('none'); }
  })();
  return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [file?.file_hash, readOnly]);

 const togglePubShare = async () => {
  if (!file || !pubShare || pubShare === 'none') return;
  setIsSaving(true);
  try {
   if (pubShare.itemId) {
    const r = await fetch(`/videobox/items/${pubShare.itemId}`, { method: 'DELETE', credentials: 'include' });
    const j = await r.json().catch(() => ({}));
    if (r.ok && j.success !== false) {
     setPubShare({ ...pubShare, itemId: null, itemStart: null });
     onUpdate(file.file_hash, file.title);
    } else {
     alert(j.error || '내보내기 해제에 실패했습니다.');
    }
   } else {
    // 항목 창 = 파일 홍보 창(BASIC 드래그와 동일 규약): 시작 max(now, publish_at), 종료 expires_at.
    const now = Date.now();
    const startMs = file.publish_at ? Math.max(now, new Date(file.publish_at).getTime()) : now;
    const r = await fetch(`/videobox/${pubShare.boxId}/items`, {
     method: 'POST', credentials: 'include',
     headers: { 'Content-Type': 'application/json' },
     body: JSON.stringify({
      fileHash: file.file_hash,
      startAt: new Date(startMs).toISOString(),
      endAt: file.expires_at ?? null,
     }),
    });
    const j = await r.json().catch(() => ({}));
    if (r.ok && j.success) {
     setPubShare({ ...pubShare, itemId: j.data?.itemId ?? -1, itemStart: startMs });
     onUpdate(file.file_hash, file.title);
    } else {
     alert(j.error || '내보내기에 실패했습니다.');
    }
   }
  } catch {
   alert('처리 중 오류가 발생했습니다.');
  } finally {
   setIsSaving(false);
  }
 };
 const [editEventLabel, setEditEventLabel] = useState(''); // 동적 라벨 (편집 중)
 const [isEditingLabel, setIsEditingLabel] = useState(false);
 const [overlayPos, setOverlayPos] = useState<number>(file?.overlay_position ?? 1); // 오버레이 위치(1~5)
 const [savingPos, setSavingPos] = useState(false);
 const [isSaving, setIsSaving] = useState(false);

 // ── 첨부 자료(격리: /attachments) — 소유자는 공개+비공개 전체를 보고 다운로드·토글·삭제 ──
 const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
 const [attachLoading, setAttachLoading] = useState(false);
 const [attachBusy, setAttachBusy] = useState<number | null>(null);
 useEffect(() => {
  if (!file) { setAttachments([]); return; }
  let alive = true;
  setAttachLoading(true);
  fetch(`/attachments/by-file/${encodeURIComponent(file.file_hash)}`, { credentials: 'include' })
   .then((r) => (r.ok ? r.json() : { data: [] }))
   .then((d) => { if (alive) setAttachments(d.data ?? []); })
   .catch(() => { if (alive) setAttachments([]); })
   .finally(() => { if (alive) setAttachLoading(false); });
  return () => { alive = false; };
 }, [file?.file_hash]);
 const toggleAttachment = async (a: AttachmentItem) => {
  setAttachBusy(a.id);
  try {
   const r = await fetch(`/attachments/attachment/${a.id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    credentials: 'include', body: JSON.stringify({ is_public: !a.isPublic }),
   });
   if (r.ok) setAttachments((prev) => prev.map((x) => (x.id === a.id ? { ...x, isPublic: !a.isPublic } : x)));
  } finally { setAttachBusy(null); }
 };
 const deleteAttachment = async (a: AttachmentItem) => {
  if (!window.confirm(`'${a.filename}' 첨부를 삭제할까요?`)) return;
  setAttachBusy(a.id);
  try {
   const r = await fetch(`/attachments/attachment/${a.id}`, { method: 'DELETE', credentials: 'include' });
   if (r.ok) setAttachments((prev) => prev.filter((x) => x.id !== a.id));
  } finally { setAttachBusy(null); }
 };
 // 영상 첨부 인라인 재생(확인용) — blob→objectURL(50MB 상한이라 스트리밍 불필요).
 const [attPlaying, setAttPlaying] = useState<{ id: number; url: string } | null>(null);
 const attPlayUrlRef = useRef<string | null>(null);
 useEffect(() => () => { if (attPlayUrlRef.current) URL.revokeObjectURL(attPlayUrlRef.current); }, []);
 const playAttachment = async (a: AttachmentItem) => {
  if (attPlaying?.id === a.id) {
   if (attPlayUrlRef.current) { URL.revokeObjectURL(attPlayUrlRef.current); attPlayUrlRef.current = null; }
   setAttPlaying(null); return;
  }
  try {
   const r = await fetch(`/attachments/attachment/${a.id}/download`, { credentials: 'include' });
   if (!r.ok) return;
   const blob = new Blob([await r.blob()], { type: attVideoMime(a.filename) });
   const url = URL.createObjectURL(blob);
   if (attPlayUrlRef.current) URL.revokeObjectURL(attPlayUrlRef.current);
   attPlayUrlRef.current = url; setAttPlaying({ id: a.id, url });
  } catch { /* 무시 — 내려받기로 확인 */ }
 };
 const [variants, setVariants] = useState<VariantEntry[]>([]);
 const [selectedVariant, setSelectedVariant] = useState<VariantEntry | null>(null);
 const [isUploadingVariant, setIsUploadingVariant] = useState(false);
 const [variantUploadPct, setVariantUploadPct] = useState(0);
 const [variantError, setVariantError] = useState<string | null>(null);
 const [variantNotice, setVariantNotice] = useState<string | null>(null);
 // 기존 main 으로부터 새 variant 트랜스코딩 생성 — 업로드 후에도 추가 가능
 const [showVariantGen, setShowVariantGen] = useState(false);
 const [recommendedVariants, setRecommendedVariants] = useState<Array<{ width: number; height: number; fps: number | null; resolution: string; clientNames: string[] }>>([]);
 const [selectedGenVariants, setSelectedGenVariants] = useState<Set<string>>(new Set());
 const [isGeneratingVariants, setIsGeneratingVariants] = useState(false);
 // 송출 통계 — 접힌 상태로 두고 펼칠 때 1회 로드(소유자 전용, readOnly 면 미노출).
 const [statsOpen, setStatsOpen] = useState(false);
 const [stats, setStats] = useState<{
  total: number; recent30: number;
  byFamily: { familyHash: string | null; familyName: string; isMine: boolean; plays: number; lastAt: string | null }[];
  daily: { day: string; plays: number }[];
 } | null>(null);
 const [statsLoading, setStatsLoading] = useState(false);
 // 파일이 바뀌면 통계 초기화 — 지연 로드(1회) 상태가 남아 **다른 영상의 숫자**가
 // 그대로 보이던 버그(2026-09-13 실사고: 새 영상에 848회 표시, 서버 실측 0회).
 useEffect(() => {
  setStats(null);
  setStatsOpen(false);
  setStatsLoading(false);
 }, [file?.file_hash]);
 // <video> 가 codec 미지원 등으로 로드 실패한 경우 fallback UI 로 전환.
 // iPhone 11 Safari (A13) 는 AV1 디코드 미지원 — onError 가 MEDIA_ERR_SRC_NOT_SUPPORTED 로 발화.
 const [playbackUnsupported, setPlaybackUnsupported] = useState(false);
 // 재생 실패 원인 구분 — 'codec'(브라우저 미지원) vs 'denied'(권한/404). 종전엔 403 도
 // "브라우저 미지원"으로 오진 표기됐다(2026-08-30). onError 시 HEAD 로 상태를 확인해 분류.
 const [playbackErrorKind, setPlaybackErrorKind] = useState<'codec' | 'denied'>('codec');
 // 원본(main) 미디어 정보 — 코덱/해상도/fps. 목록 엔드포인트마다 필드가 제각각이라
 // (my-files 는 codec 을 안 실어준다) 모달이 열릴 때 transcode-status 로 한 번 조회한다.
 // 엔드포인트 N 곳을 고치는 대신 한 곳에서 받아 어디서 열려도 동일하게 보이게 한다.
 const [mediaInfo, setMediaInfo] = useState<{ codec: string | null; resolution: string | null; fps: number | null } | null>(null);
 const variantFileInputRef = useRef<HTMLInputElement>(null);

 // 대표 트랜스코딩 상태 — WS push로 실시간 갱신
 const mainEntry = useTranscodeEntry(file?.file_hash ?? '', null);
 const seedTranscode = useFileTranscodeStore(s => s.seed);

 const currentStatus: TranscodeStatus =
  mainEntry?.status ?? file?.transcode_status ?? 'done';

 // 모달 mount 시 1회 시드(HTTP) — 이후 갱신은 WS만 의존
 useEffect(() => {
  if (!file) return;
  setIsEditingTitle(false);
  setVariantError(null);
  setPlaybackUnsupported(false);

  const isVideoFile = file.file_type?.startsWith('video/');
  if (!isVideoFile) return;

  // done 상태면 video metadata 로드
  if ((file.transcode_status ?? 'done') === 'done') {
   const video = document.createElement('video');
   video.src = file.file_url;
   video.onloadedmetadata = () => setVideoDuration(video.duration);
  }

  // store에 이미 entry가 있으면 시드 생략 (WS가 더 fresh)
  const existing = useFileTranscodeStore.getState().get(file.file_hash, null);
  if (existing) return;

  // pending/processing/failed 상태일 때만 HTTP로 1회 시드
  const initialStatus = file.transcode_status ?? 'done';
  if (initialStatus === 'done') return;

  let cancelled = false;
  fetch(`/mofile/${file.file_hash}/transcode-status`, { credentials: 'include' })
   .then(r => r.ok ? r.json() : null)
   .then(json => {
    if (cancelled || !json?.data) return;
    const d = json.data;
    seedTranscode(file.file_hash, null, {
     status: d.transcode_status,
     progress: d.transcode_progress ?? 0,
     etaSec: d.transcode_eta_sec ?? null,
     codec: d.codec,
     fps: d.fps,
     error: d.transcode_error ?? undefined,
    });
   })
   .catch(() => {});
  return () => { cancelled = true; };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [file?.file_hash]);

 // 원본 미디어 정보(코덱/해상도/fps) 조회 — 위 진행상태 시드와 달리 **done 이어도** 가져온다.
 // 진행상태 머신(seedTranscode)은 건드리지 않는다: 별개 관심사이고, done 파일에 시드를 넣으면
 // 진행 UI 동작에 영향이 갈 수 있다.
 useEffect(() => {
  if (!file?.file_hash) { setMediaInfo(null); return; }
  let cancelled = false;
  setMediaInfo(null);
  fetch(`/mofile/${file.file_hash}/transcode-status`, { credentials: 'include' })
   .then(r => r.ok ? r.json() : null)
   .then(json => {
    if (cancelled || !json?.data) return;
    setMediaInfo({
     codec: json.data.codec ?? null,
     resolution: json.data.resolution ?? null,
     fps: json.data.fps ?? null,
    });
   })
   .catch(() => {});
  return () => { cancelled = true; };
 }, [file?.file_hash]);

 // done 진입 시 video metadata 재시도
 useEffect(() => {
  if (file && currentStatus === 'done' && videoDuration === null && file.file_type?.startsWith('video/')) {
   const video = document.createElement('video');
   video.src = file.file_url;
   video.onloadedmetadata = () => setVideoDuration(video.duration);
  }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [currentStatus]);

 // variant 변경 시 fallback 상태 reset — 새 src 는 다시 시도해 봐야 함
 useEffect(() => {
  setPlaybackUnsupported(false);
 }, [selectedVariant]);

 // 변형 목록 로드 (영상 파일만)
 const loadVariants = React.useCallback(async () => {
  if (!file || !file.file_type?.startsWith('video/')) return;
  try {
   const resp = await fetch(`/mofile/${file.file_hash}/variants`, { credentials: 'include' });
   if (!resp.ok) return;
   const json = await resp.json();
   setVariants(json.data || []);
  } catch { /* ignore */ }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [file?.file_hash]);

 useEffect(() => {
  setVariants([]);
  setSelectedVariant(null);  // 새 파일 모달 열 때 대표로 리셋
  loadVariants();
 }, [loadVariants]);

 // WS로 진행률이 갱신되면 변형 목록도 갱신
 const variantStateMap = useFileTranscodeStore(s => s.entries);
 useEffect(() => {
  if (!file) return;
  // 변형 중 하나라도 done으로 전환되었으면 목록 새로고침 (file_size·status 갱신용)
  for (const v of variants) {
   if (v.kind !== 'variant' || !v.resolution) continue;
   // 회전본은 같은 해상도라도 별개 아티팩트 → variantKey(rotation 포함)로 키잉해야
   // 회전/미회전 상태가 같은 store 키를 두고 충돌(UI 완료↔변환 깜빡임)하지 않는다.
   const entry = variantStateMap.get(`${file.file_hash}::${variantSlug(v.resolution, v.rotation, v.fps)}`);
   if (entry && entry.status !== v.transcode_status) {
    loadVariants();
    return;
   }
  }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [variantStateMap, file?.file_hash]);

 // 화해(reconcile) 폴링 — transcode_complete/failed WS 푸시를 놓치면 진행률 바가 마지막
 // %(예: 46%)에 얼어붙는다(실제 DB 는 done). 모달이 열린 동안 실제 상태를 주기 재조회해,
 // 놓친 terminal 전이를 store 에 반영하고 변형 목록도 갱신한다. 모두 terminal 이면 자동 정지.
 useEffect(() => {
  if (!file || !file.file_type?.startsWith('video/')) return;
  const hash = file.file_hash;
  const isTerminal = (s?: string) => s === 'done' || s === 'failed';
  let timer: number | null = null;
  const tick = async () => {
   try {
    const resp = await fetch(`/mofile/${hash}/variants`, { credentials: 'include' });
    if (!resp.ok) return;
    const fresh: VariantEntry[] = (await resp.json()).data || [];
    setVariants(fresh);
    const store = useFileTranscodeStore.getState();
    let allTerminal = true;
    for (const v of fresh) {
     const res = v.kind === 'variant' && v.resolution ? variantSlug(v.resolution, v.rotation) : null;
     if (!isTerminal(v.transcode_status)) allTerminal = false;
     const cur = store.get(hash, res);
     // store 는 진행 중인데 DB 는 terminal → 놓친 이벤트. 화해.
     if (isTerminal(v.transcode_status) && cur && !isTerminal(cur.status)) {
      store.seed(hash, res, {
       status: v.transcode_status,
       progress: v.transcode_status === 'done' ? 100 : (v.transcode_progress ?? cur.progress),
      });
     }
    }
    if (allTerminal && fresh.length > 0 && timer !== null) {
     window.clearInterval(timer);
     timer = null;
    }
   } catch { /* ignore */ }
  };
  timer = window.setInterval(tick, 4000);
  return () => { if (timer !== null) window.clearInterval(timer); };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [file?.file_hash]);

 const handleVariantUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  if (!file) return;
  const f = e.target.files?.[0];
  if (!f) return;
  setVariantError(null);
  setVariantNotice(null);
  setIsUploadingVariant(true);
  setVariantUploadPct(0);
  try {
   const fd = new FormData();
   fd.append('file', f);
   // 수십 초짜리 전송이라 바이트 진행률을 보여준다(공용 헬퍼).
   const { status, json } = await uploadWithProgress({
    url: `/mofile/${file.file_hash}/variants`, formData: fd,
    onProgress: (pct) => setVariantUploadPct(pct),
   }).promise;
   if (status < 200 || status >= 300) throw new Error(json?.error || '올리기 실패');
   await loadVariants();
  } catch (err: any) {
   setVariantError(err.message || '화면 맞춤본 올리기 실패');
  } finally {
   setIsUploadingVariant(false);
   if (variantFileInputRef.current) variantFileInputRef.current.value = '';
  }
 };

 const handleVariantDelete = async (resolution: string) => {
  if (!file) return;
  if (!confirm(`${resolution} 화면 맞춤본을 삭제하시겠습니까?`)) return;
  try {
   const resp = await fetch(`/mofile/${file.file_hash}/variants/${resolution}`, {
    method: 'DELETE',
    credentials: 'include',
   });
   if (!resp.ok) {
    const j = await resp.json();
    setVariantError(j.error || '삭제 실패');
    return;
   }
   await loadVariants();
  } catch (err: any) {
   setVariantError(err.message || '삭제 실패');
  }
 };

 // 다른 파일로 모달이 바뀌면 위치 선택 상태를 그 파일 값으로 동기화(useState init 은 최초 1회만).
 // ⚠️ 이 훅은 반드시 아래 early return 앞에 둔다 — 뒤에 두면 file 유무에 따라 훅 개수가 달라져
 // React #310(Rendered more/fewer hooks)로 모달이 통째로 깨진다.
 useEffect(() => {
  setOverlayPos(file?.overlay_position ?? 1);
 }, [file?.file_hash, file?.overlay_position]);

 if (!file) return null;

 const isVideo = file.file_type?.startsWith('video/');
 const isImage = file.file_type?.startsWith('image/');

 const formatDuration = (seconds: number | null | undefined): string | null => {
  // fragmented mp4(+empty_moov)는 HTMLVideoElement.duration이 Infinity로 나오는 경우가 있다.
  // DB ffprobe 값이 없고 <video>에서도 못 읽었으면 아무것도 표시하지 않는다.
  if (seconds === null || seconds === undefined) return null;
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds % 3600 / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}시간 ${m}분 ${s}초`;
  if (m > 0) return `${m}분 ${s}초`;
  return `${s}초`;
 };

 // DB 저장값(ffprobe 결과) 우선, 없으면 <video>.duration 사용
 const durationLabel = formatDuration(file.duration) ?? formatDuration(videoDuration);

 const handleDelete = async () => {
  if (!confirm(`"${file.title}" 파일을 삭제하시겠습니까?`)) return;

  try {
   const response = await fetch(`/mofile/${file.file_hash}`, {
    method: 'DELETE',
    credentials: 'include'
   });

   if (response.ok) {
    onDelete();
    onClose();
   } else {
    alert('파일 삭제에 실패했습니다.');
   }
  } catch (error) {
   console.error('Delete error:', error);
   alert('파일 삭제 중 오류가 발생했습니다.');
  }
 };

 const handleStartEdit = () => {
  setEditTitle(file.title);
  setIsEditingTitle(true);
 };

 const startEditEvent = () => {
  setEditEventStart(isoToLocalInput(file.event_start_at));
  // 만료: 저장된 값이 있으면 그대로, 없으면(=행사 시작 미입력 기본) 체크 해제.
  setEditExpires(isoToLocalInput(file.expires_at));
  setEditExpiresEnabled(!!file.expires_at);
  // 행사 시간은 저장 필드가 아니라 시작·종료에서 역산한다(단일 진실원천 = 시작·종료).
  setEditDurationMin(String(minutesBetween(isoToLocalInput(file.event_start_at), isoToLocalInput(file.expires_at)) ?? ''));
  setIsEditingEvent(true);
 };

 // 행사 시작이 바뀌면 **행사 시간을 유지한 채** 종료를 옮긴다(시작만 미루는 게 흔한 편집이다).
 // 행사 시간이 비어 있으면 기본 60분 — 종전 "만료 = 시작 + 1시간" 규칙과 같은 결과다.
 // 빈값으로 지우면 만료도 해제.
 const onChangeEventStart = (v: string) => {
  const r = onStartChanged(v, editDurationMin);
  setEditEventStart(r.start);
  setEditExpires(r.end);
  setEditDurationMin(r.durationMin);
  setEditExpiresEnabled(!!r.start);
 };

 // 행사 시간 입력 → 종료 시각 계산. 시작이 없으면 계산할 수 없으므로 값만 들고 있는다.
 const onChangeDuration = (v: string) => {
  setEditDurationMin(v);
  const end = endFromDuration(editEventStart, v);
  if (end !== null) { setEditExpiresEnabled(true); setEditExpires(end); }
 };

 // 종료 시각 입력 → 행사 시간 역산. 종료가 시작보다 이르면 음수가 나오는데, 고쳐주지 않고
 // 그대로 보여준다 — 타이핑 중간값을 임의로 바꾸면 입력이 튄다. 경고만 띄우고 저장은 막는다.
 const onChangeExpires = (v: string) => {
  setEditExpires(v);
  setEditDurationMin(durationFromEnd(editEventStart, v));
 };

 // 시작 입력에서 포커스가 빠질 때, 종료가 시작보다 이르면 **시작 + 기본 행사 시간**으로 스냅한다.
 // 종료를 직접 이르게 고쳐놓고 시작 칸만 스쳐 지나가면(onChange 미발화) 잘못된 상태가 그대로
 // 남는데, 그 창을 닫는다.
 // ⚠️ 시작과 '같게'(0분) 맞추지 않는 이유: expires_at == event_start_at 은 "행사가 시작하는 그
 //    순간 만료"라 그 영상은 사실상 재생되지 않는다. 오류를 고친 자리에 다른 함정을 두지 않는다.
 const onBlurEventStart = () => {
  const snap = snapOnStartBlur(editEventStart, editExpires);
  if (snap) { setEditExpires(snap.end); setEditDurationMin(snap.durationMin); }
 };

 // 종료가 시작보다 **이르면** 저장 불가. 같은 시각(0분)은 허용한다 — 위 스냅의 결과이고
 // "같음"은 "이전"이 아니다.
 const durationInvalid = editExpiresEnabled && isRangeInvalid(editEventStart, editExpires);

 const startEditLabel = () => {
  setEditEventLabel(file.event_label ?? '');
  setIsEditingLabel(true);
 };

 // 오버레이 위치 선택 즉시 저장(PUT). 실패 시 이전 값으로 롤백.
 const handleSelectOverlayPos = async (pos: number) => {
  if (!file || pos === overlayPos || savingPos) return;
  const prev = overlayPos;
  setOverlayPos(pos);
  setSavingPos(true);
  try {
   const response = await fetch(`/mofile/${file.file_hash}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ title: file.title, overlay_position: pos }),
   });
   const data = await response.json();
   if (data.success) {
    file.overlay_position = pos;
    // 부모 목록 갱신 트리거 — 행사·라벨·제목 저장과 같은 규약(제목은 안 바뀐다).
    // ⚠️ 이게 없으면 위 객체 변이에만 기대게 되는데, 부모가 매번 새 객체를 만들어
    //    넘기는 화면(계층도 videoToMoFile)에서는 변이가 임시 객체에 남고 사라진다.
    onUpdate(file.file_hash, file.title);
   } else {
    setOverlayPos(prev);
    alert(data.error || '오버레이 위치 저장에 실패했습니다.');
   }
  } catch {
   setOverlayPos(prev);
   alert('오버레이 위치 저장 중 오류가 발생했습니다.');
  } finally {
   setSavingPos(false);
  }
 };

 const handleSaveLabel = async () => {
  const next = editEventLabel.trim() ? editEventLabel.trim().slice(0, 60) : null;
  const cur = file.event_label ?? null;
  if (next === cur) { setIsEditingLabel(false); return; }
  setIsSaving(true);
  try {
   const response = await fetch(`/mofile/${file.file_hash}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ title: file.title, event_label: next })
   });
   if (response.ok) {
    file.event_label = next;
    // 부모 목록 갱신 트리거(제목 무변경) — event_start 저장과 동일 이유(닫고 재열 때 stale).
    onUpdate(file.file_hash, file.title);
    setIsEditingLabel(false);
   } else {
    const data = await response.json();
    alert(data.error || '동적 라벨 저장 실패');
   }
  } catch {
   alert('동적 라벨 저장 중 오류가 발생했습니다.');
  } finally {
   setIsSaving(false);
  }
 };

 const handleSaveEvent = async () => {
  const newEventIso = editEventStart ? new Date(editEventStart).toISOString() : null;
  const curEventIso = file.event_start_at ? new Date(file.event_start_at).toISOString() : null;
  // 만료: 체크됐고 값 있으면 그 값, 아니면 null(해제).
  const newExpiresIso = editExpiresEnabled && editExpires ? new Date(editExpires).toISOString() : null;
  const curExpiresIso = file.expires_at ? new Date(file.expires_at).toISOString() : null;
  if (newEventIso === curEventIso && newExpiresIso === curExpiresIso) { setIsEditingEvent(false); return; }
  setIsSaving(true);
  try {
   // PUT 은 title 필수 — 현재 제목 그대로 함께 보냄(제목 무변경). 행사 시작+만료 함께 저장.
   const response = await fetch(`/mofile/${file.file_hash}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ title: file.title, event_start_at: newEventIso, expires_at: newExpiresIso })
   });
   if (response.ok) {
    file.event_start_at = newEventIso;
    file.expires_at = newExpiresIso;
    // 부모 목록(myVideos 등) 갱신 트리거 — 안 하면 닫았다 다시 열 때 stale 소스에서
    // 재생성되어 수정이 사라져 보인다. 제목은 무변경이라 현재 값 그대로 전달.
    onUpdate(file.file_hash, file.title);
    setIsEditingEvent(false);
   } else {
    const data = await response.json();
    alert(data.error || '행사 시작 시각 저장에 실패했습니다.');
   }
  } catch (error) {
   console.error('Event save error:', error);
   alert('저장 중 오류가 발생했습니다.');
  } finally {
   setIsSaving(false);
  }
 };

 const handleCancelEdit = () => {
  setIsEditingTitle(false);
  setEditTitle('');
 };

 const handleSaveTitle = async () => {
  const trimmed = editTitle.trim();
  if (!trimmed || trimmed === file.title) {
   setIsEditingTitle(false);
   return;
  }

  setIsSaving(true);
  try {
   const response = await fetch(`/mofile/${file.file_hash}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ title: trimmed })
   });

   if (response.ok) {
    onUpdate(file.file_hash, trimmed);
    setIsEditingTitle(false);
   } else {
    const data = await response.json();
    alert(data.error || '이름 수정에 실패했습니다.');
   }
  } catch (error) {
   console.error('Rename error:', error);
   alert('이름 수정 중 오류가 발생했습니다.');
  } finally {
   setIsSaving(false);
  }
 };

 const handleTitleKeyDown = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter') {
   handleSaveTitle();
  } else if (e.key === 'Escape') {
   handleCancelEdit();
  }
 };

 return (
  <div
   className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
   onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
   <div className="bg-white p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
    <div className="flex justify-between items-start mb-4">
     {isEditingTitle ? (
      <div className="flex items-center gap-2 flex-1 mr-4">
       <input
        type="text"
        value={editTitle}
        onChange={(e) => setEditTitle(e.target.value)}
        onKeyDown={handleTitleKeyDown}
        autoFocus
        disabled={isSaving}
        className="text-2xl font-bold flex-1 px-2 py-1 border border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
       />
       <button
        onClick={handleSaveTitle}
        disabled={isSaving}
        className="px-3 py-1 bg-blue-500 text-white text-sm hover:bg-blue-600 disabled:opacity-50"
       >
        {isSaving ? '저장...' : '저장'}
       </button>
       <button
        onClick={handleCancelEdit}
        disabled={isSaving}
        className="px-3 py-1 bg-gray-200 text-gray-700 text-sm hover:bg-gray-300"
       >
        취소
       </button>
      </div>
     ) : (
      <div className="flex items-center gap-2">
       <h2 className="text-2xl font-bold">{file.title}</h2>
       {!readOnly && <button
        onClick={handleStartEdit}
        className="text-gray-400 hover:text-blue-500"
        title="이름 수정"
       >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
       </button>}
      </div>
     )}
     <button
      className="text-gray-400 hover:text-gray-600"
      onClick={onClose}
     >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
     </button>
    </div>

    {/* 빈 슬롯에 넣기 — 계층도에서 열렸을 때만. 모바일 드래그가 불안정해 클릭 대안 제공. */}
    {onAddToEmptySlot && file && (
     <div className="mb-4">
      <button
       type="button"
       disabled={slotting}
       onClick={async () => {
        setSlotting(true); setSlotMsg(null);
        try {
         const r = await onAddToEmptySlot(file.file_hash);
         setSlotMsg({ ok: r.ok, text: r.message });
        } catch (e: any) {
         setSlotMsg({ ok: false, text: e?.message || '슬롯에 넣지 못했습니다.' });
        } finally { setSlotting(false); }
       }}
       className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium flex items-center justify-center gap-2"
      >
       <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
       {slotting ? '넣는 중...' : '빈 슬롯에 넣기'}
      </button>
      {slotMsg && (
       <p className={`mt-1 text-sm ${slotMsg.ok ? 'text-green-600' : 'text-red-600'}`}>{slotMsg.text}</p>
      )}
     </div>
    )}

    {/* 행사 시작 시각 — 이름 수정 모드일 때 입력, 아니면 표시. 값 있으면 재생 시 카운트다운 오버레이 대상. */}
    <div className="mb-4 flex items-center flex-wrap gap-2 text-sm">
     <span className="font-medium text-gray-700">행사 시작:</span>
     {isEditingEvent ? (
      <>
       <input
        type="datetime-local"
        value={editEventStart}
        onChange={(e) => onChangeEventStart(e.target.value)}
        onBlur={onBlurEventStart}
        disabled={isSaving}
        autoFocus
        className="border border-gray-300 px-2 py-1"
       />
       {editEventStart && (
        <button type="button" onClick={() => onChangeEventStart('')} className="text-xs text-gray-400 hover:text-red-500">지우기</button>
       )}
       {/* 행사 시간 — 시작·종료 사이. 입력하면 종료가 계산되고, 종료를 고치면 여기가 역산된다. */}
       {editExpiresEnabled && (
        <label className="flex items-center gap-1 ml-2 text-gray-700">
         행사 시간
         <input
          type="number"
          min={1}
          step={5}
          value={editDurationMin}
          onChange={(e) => onChangeDuration(e.target.value)}
          disabled={isSaving || !editEventStart}
          title={editEventStart ? '' : '행사 시작 시각을 먼저 입력하세요'}
          className="border border-gray-300 px-2 py-1 w-20"
         />
         분
         {humanizeMinutes(Number(editDurationMin)) && (
          <span className="text-xs text-gray-400">({humanizeMinutes(Number(editDurationMin))})</span>
         )}
        </label>
       )}
       {/* 만료(=종료) 시각 — 행사 시작 입력 시 자동 체크+행사시간 적용. 수동 체크해제/수정 가능. */}
       <label className="flex items-center gap-1 ml-2 text-gray-700">
        <input
         type="checkbox"
         checked={editExpiresEnabled}
         onChange={(e) => {
          setEditExpiresEnabled(e.target.checked);
          if (e.target.checked && !editExpires) {
           setEditExpires(localInputPlusMinutes(editEventStart, DEFAULT_DURATION_MIN));
           setEditDurationMin(String(DEFAULT_DURATION_MIN));
          }
         }}
         disabled={isSaving}
        />
        홍보 종료
       </label>
       {editExpiresEnabled && (
        <input
         type="datetime-local"
         value={editExpires}
         onChange={(e) => onChangeExpires(e.target.value)}
         disabled={isSaving}
         className="border border-gray-300 px-2 py-1"
        />
       )}
       {durationInvalid && (
        <span className="text-xs text-red-500">종료가 시작보다 이릅니다</span>
       )}
       <button type="button" onClick={handleSaveEvent} disabled={isSaving || durationInvalid} className="px-3 py-1 bg-blue-500 text-white text-sm hover:bg-blue-600 disabled:opacity-50">
        {isSaving ? '저장...' : '저장'}
       </button>
       <button type="button" onClick={() => setIsEditingEvent(false)} disabled={isSaving} className="px-3 py-1 bg-gray-200 text-gray-700 text-sm hover:bg-gray-300">취소</button>
      </>
     ) : (
      <>
       <span className="text-gray-600">
        {file.event_start_at ? new Date(file.event_start_at).toLocaleString('ko-KR') : '미설정'}
        {file.expires_at && (
         <span className="text-gray-400">
          {' · 홍보 종료 '}{new Date(file.expires_at).toLocaleString('ko-KR')}
          {/* 행사 시간은 저장 필드가 아니라 시작·종료에서 역산해 보여준다. */}
          {humanizeMinutes(minutesBetween(isoToLocalInput(file.event_start_at), isoToLocalInput(file.expires_at)))
           && ` (${humanizeMinutes(minutesBetween(isoToLocalInput(file.event_start_at), isoToLocalInput(file.expires_at)))})`}
         </span>
        )}
       </span>
       {!readOnly && <button type="button" onClick={startEditEvent} className="text-gray-400 hover:text-blue-500" title={file.event_start_at ? '행사 시작 시각 수정' : '행사 시작 시각 설정'}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
       </button>}
      </>
     )}
    </div>

    {/* 홍보 시작(publish_at) — 만료의 거울. 미래면 슬롯·박스 어디에 있어도 그때까지 재생 스킵. */}
    <div className="mb-4 flex items-center flex-wrap gap-2 text-sm">
     <span className="font-medium text-gray-700">홍보 시작:</span>
     {isEditingPublish ? (
      <>
       <input
        type="datetime-local"
        value={editPublish}
        onChange={(e) => setEditPublish(e.target.value)}
        disabled={isSaving}
        autoFocus
        className="border border-gray-300 px-2 py-1"
       />
       {editPublish && (
        <button type="button" onClick={() => setEditPublish('')} className="text-xs text-gray-400 hover:text-red-500">지우기(즉시)</button>
       )}
       <button
        type="button"
        disabled={isSaving}
        onClick={async () => {
         const newIso = editPublish ? new Date(editPublish).toISOString() : null;
         const curIso = file.publish_at ? new Date(file.publish_at).toISOString() : null;
         if (newIso === curIso) { setIsEditingPublish(false); return; }
         setIsSaving(true);
         try {
          const r = await fetch(`/mofile/${file.file_hash}`, {
           method: 'PUT',
           headers: { 'Content-Type': 'application/json' },
           credentials: 'include',
           body: JSON.stringify({ title: file.title, publish_at: newIso }),
          });
          if (r.ok) {
           file.publish_at = newIso;
           onUpdate(file.file_hash, file.title);
           setIsEditingPublish(false);
          } else {
           const d = await r.json().catch(() => ({}));
           alert(d.error || '홍보 시작 저장에 실패했습니다.');
          }
         } catch { alert('저장 중 오류가 발생했습니다.'); }
         finally { setIsSaving(false); }
        }}
        className="px-3 py-1 bg-blue-500 text-white text-sm hover:bg-blue-600 disabled:opacity-50"
       >{isSaving ? '저장...' : '저장'}</button>
       <button type="button" onClick={() => setIsEditingPublish(false)} disabled={isSaving} className="px-3 py-1 bg-gray-200 text-gray-700 text-sm hover:bg-gray-300">취소</button>
      </>
     ) : (
      <>
       <span className="text-gray-600">
        {file.publish_at ? new Date(file.publish_at).toLocaleString('ko-KR') : '즉시'}
        {file.publish_at && new Date(file.publish_at).getTime() > Date.now() && (
         <span className="text-amber-600 text-xs ml-1">(예약 — 그 전에는 재생되지 않음)</span>
        )}
       </span>
       {!readOnly && <button type="button"
        onClick={() => { setEditPublish(isoToLocalInput(file.publish_at)); setIsEditingPublish(true); }}
        className="text-gray-400 hover:text-blue-500"
        title={file.publish_at ? '홍보 시작 수정' : '홍보 시작 예약'}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
       </button>}
      </>
     )}
    </div>

    {/* 첨부 자료 — 영상 키 첨부(격리 모듈). 재생과 무관한 문서/파일. 공개/비공개 배지·다운로드·토글·삭제. */}
    <div className="mb-4">
     <div className="flex items-center justify-between mb-1">
      <span className="font-medium text-gray-700 text-sm">첨부 자료</span>
      {attachLoading && <span className="text-xs text-gray-400">불러오는 중…</span>}
     </div>
     {attachments.length === 0 ? (
      <p className="text-xs text-gray-400">첨부된 자료가 없습니다.</p>
     ) : (
      <ul className="divide-y divide-gray-100 border border-gray-100 rounded">
       {attachments.map((a) => (
        <li key={a.id} className="px-2 py-1.5 text-sm">
         <div className="flex items-center gap-2">
          <span className={`text-[11px] px-1.5 py-0.5 rounded shrink-0 ${a.isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
           {a.isPublic ? '공개' : '비공개'}
          </span>
          {attIsVideo(a.filename) && <AttVideoThumb id={a.id} />}
          <span className="flex-1 truncate text-gray-700" title={a.filename}>{a.filename}</span>
          <span className="text-xs text-gray-400 shrink-0">{formatBytes(a.byteSize)}</span>
          {attIsVideo(a.filename) && (
           <button type="button" onClick={() => playAttachment(a)}
            className="text-xs text-indigo-600 hover:underline shrink-0">{attPlaying?.id === a.id ? '■ 닫기' : '▶ 재생'}</button>
          )}
          <a href={`/attachments/attachment/${a.id}/download`} className="text-xs text-blue-600 hover:underline shrink-0">⬇ 내려받기</a>
          {!readOnly && (
           <>
            <button type="button" disabled={attachBusy === a.id} onClick={() => toggleAttachment(a)}
             className="text-xs text-gray-500 hover:text-green-600 shrink-0 disabled:opacity-50">{a.isPublic ? '비공개로' : '공개로'}</button>
            <button type="button" disabled={attachBusy === a.id} onClick={() => deleteAttachment(a)}
             className="text-xs text-gray-400 hover:text-red-500 shrink-0 disabled:opacity-50">삭제</button>
           </>
          )}
         </div>
         {attPlaying?.id === a.id && (
          <video src={attPlaying.url} controls autoPlay className="w-full mt-2 rounded bg-black" />
         )}
        </li>
       ))}
      </ul>
     )}
    </div>

    {/* 제휴 송출 — 내 'Public' 박스 포함 여부(BASIC 드래그와 동일 효과, 모바일 대안).
        Public 박스가 없는 계정(제휴 미사용)에는 행 자체를 감춘다. */}
    {!readOnly && pubShare !== null && pubShare !== 'none' && (
     <div className="mb-4 flex items-center flex-wrap gap-2 text-sm">
      <span className="font-medium text-gray-700">제휴 송출:</span>
      <label className="flex items-center gap-1.5 text-gray-600 cursor-pointer">
       <input
        type="checkbox"
        checked={!!pubShare.itemId}
        disabled={isSaving}
        onChange={() => void togglePubShare()}
       />
       제휴 장소 화면에도 내보내기
      </label>
      {!!pubShare.itemId && (() => {
       {/* 제휴 항목의 실효 시작 = max(항목 창, 파일 홍보 시작) — 제휴 목록 카드와 같은 사영.
           파일 홍보 시작을 나중에 지워도 항목 창은 남는다 — 어긋남을 여기서 드러낸다. */}
       const eff = Math.max(pubShare.itemStart ?? 0,
        file.publish_at ? new Date(file.publish_at).getTime() : 0);
       return eff > Date.now() ? (
        <span className="text-xs text-amber-600">
         {new Date(eff).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })} 부터 나가요
         — 지금부터 내보내려면 체크를 껐다 켜세요
        </span>
       ) : (
        <span className="text-xs text-gray-400">체크 해제하면 제휴 화면에서 내려갑니다</span>
       );
      })()}
     </div>
    )}

    {/* 동적 라벨 — 장소 등 짧은 정보. 값 있으면 재생 시 오버레이 표시. */}
    <div className="mb-4 text-sm">
     <div className="flex items-center flex-wrap gap-2">
     <span className="font-medium text-gray-700">동적 라벨:</span>
     {isEditingLabel ? (
      <>
       <textarea
        value={editEventLabel}
        maxLength={60}
        rows={2}
        placeholder="예: 강남역 광장 (Enter=줄바꿈, 코너 위치에서 여러 줄)"
        onChange={(e) => setEditEventLabel(e.target.value)}
        disabled={isSaving}
        autoFocus
        className="border border-gray-300 px-2 py-1 resize-none align-top"
       />
       {editEventLabel && (
        <button type="button" onClick={() => setEditEventLabel('')} className="text-xs text-gray-400 hover:text-red-500">지우기</button>
       )}
       <button type="button" onClick={handleSaveLabel} disabled={isSaving} className="px-3 py-1 bg-blue-500 text-white text-sm hover:bg-blue-600 disabled:opacity-50">
        {isSaving ? '저장...' : '저장'}
       </button>
       <button type="button" onClick={() => setIsEditingLabel(false)} disabled={isSaving} className="px-3 py-1 bg-gray-200 text-gray-700 text-sm hover:bg-gray-300">취소</button>
      </>
     ) : (
      <>
       <span className="text-gray-600 whitespace-pre-line">{file.event_label || '미설정'}</span>
       {!readOnly && <button type="button" onClick={startEditLabel} className="text-gray-400 hover:text-blue-500" title={file.event_label ? '동적 라벨 수정' : '동적 라벨 설정'}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
       </button>}
      </>
     )}
     </div>
     <p className="text-xs text-gray-400 mt-1">영상에 날짜나 장소가 없을 땐, 여기를 이용하세요.</p>
    </div>

    {/* 오버레이 위치 — 항상 노출(2026-09-17). 종전엔 행사·라벨 있을 때만 보였는데,
        라벨을 지우면 폼이 사라져 못 바꾸는 함정 + 시스템 출력(카운트다운 날짜)과
        슬롯 라벨도 이 위치를 쓴다. 클릭 즉시 저장(PUT).
        위치 1=현재 스타일(상단 중앙), 2~5=코너(ExtraBold 2줄). 가이드 이미지로 미리보기. */}
    {!readOnly && (
     <div className="mb-4 text-sm">
      <div className="flex items-center gap-2 mb-2">
       <span className="font-medium text-gray-700">오버레이 위치</span>
       {savingPos && <span className="text-xs text-gray-400">저장 중…</span>}
      </div>
      <div className="flex flex-wrap gap-2">
       {[1, 2, 3, 4, 5].map((pos) => (
        <button
         key={pos}
         type="button"
         onClick={() => handleSelectOverlayPos(pos)}
         disabled={savingPos}
         title={OVERLAY_POS_LABELS[pos]}
         className={`overflow-hidden rounded border-2 transition-colors disabled:opacity-60 ${overlayPos === pos ? 'border-blue-500' : 'border-gray-200 hover:border-gray-400'}`}
        >
         <img src={`/static/overlay-pos/pos${pos}.png`} alt={OVERLAY_POS_LABELS[pos]} className="block w-[64px] h-auto" />
         <span className={`block py-0.5 text-center text-xs ${overlayPos === pos ? 'bg-blue-500 text-white' : 'text-gray-600'}`}>{OVERLAY_POS_LABELS[pos]}</span>
        </button>
       ))}
      </div>
     </div>
    )}

    <div className="mb-6">
     {isVideo ? (
      currentStatus === 'done' ? (
       <>
        {/* 재생 컴포넌트 크기를 영상 실제 비율에 맞춰 — bg-black + max-h-96 의 고정
            박스 안에서 letterbox 되면 실제 영상의 검정 패딩(스트레치 fit 변형의 검정
            여백)과 player letterbox 가 구분 안 되는 문제 해결. */}
        {(() => {
         const activeForSize =
          selectedVariant ?? variants.find(v => v.kind === 'main') ?? null;
         const aspect =
          activeForSize && activeForSize.width > 0 && activeForSize.height > 0
           ? `${activeForSize.width} / ${activeForSize.height}`
           : undefined;
         const playbackUrl = selectedVariant?.download_url ?? file.file_url;
         if (playbackUnsupported) {
          // iPhone 11 Safari 등 AV1 미지원 브라우저용 fallback.
          // 영상 codec 이 브라우저에서 디코드 불가능할 때 노출.
          return (
           <div
            className="w-full bg-gray-50 border border-gray-200 flex flex-col items-center justify-center p-6"
            style={{ aspectRatio: aspect, maxHeight: '24rem' }}
           >
            <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <div className="text-gray-700 font-semibold mb-1">
             {playbackErrorKind === 'denied' ? '미리보기를 불러오지 못했습니다' : '이 브라우저에서 미리보기를 지원하지 않습니다'}
            </div>
            <div className="text-xs text-gray-500 mb-4 text-center max-w-md">
             {playbackErrorKind === 'denied'
              ? '이 영상을 볼 권한이 없거나 파일을 찾을 수 없습니다.'
              : 'AV1 코덱이 필요합니다. iPhone 11 이전 / 구형 Safari 에서는 디코드되지 않습니다.'}
            </div>
            {playbackErrorKind === 'codec' && <a
             href={playbackUrl}
             download={file.title}
             className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >다운로드해서 보기</a>}
           </div>
          );
         }
         return (
          <video
           key={playbackUrl}  // src 변경 시 강제 리로드
           controls
           src={playbackUrl}
           onError={() => {
            // 코덱 실패와 권한 실패를 구분 — HTTP 상태로 판정(HEAD, best-effort).
            fetch(playbackUrl, { method: 'HEAD', credentials: 'include' })
             .then(r => setPlaybackErrorKind(r.ok ? 'codec' : 'denied'))
             .catch(() => setPlaybackErrorKind('codec'))
             .finally(() => setPlaybackUnsupported(true));
           }}
           style={{
            aspectRatio: aspect,
            maxHeight: '24rem',
            maxWidth: '100%',
            width: 'auto',
            display: 'block',
            margin: '0 auto',
           }}
          />
         );
        })()}
        {selectedVariant && (
         <div className="mt-1 text-xs text-gray-500 flex items-center gap-2">
          <span>재생 중: {aspectIcon(selectedVariant.width, selectedVariant.height)} {selectedVariant.resolution}{selectedVariant.fps != null && ` · ${canonFps(selectedVariant.fps)}fps`}</span>
          <button
           onClick={() => setSelectedVariant(null)}
           className="text-blue-600 hover:underline"
          >대표로 돌아가기</button>
         </div>
        )}
       </>
      ) : currentStatus === 'failed' ? (
       <div className="w-full h-64 bg-red-50 border border-red-200 flex flex-col items-center justify-center p-6">
        <svg className="w-12 h-12 text-red-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z" />
        </svg>
        <div className="text-red-700 font-semibold">게재 준비 실패</div>
        {(mainEntry?.error || file.transcode_error) && (
         <div className="text-xs text-red-600 mt-2 text-center break-all max-w-md">
          {mainEntry?.error || file.transcode_error}
         </div>
        )}
       </div>
      ) : file.thumbnail_path ? (
       /* 변환 전(api 수집=deferred)·변환 중 — 재생할 영상이 아직 없으므로 포스터(썸네일)를 보여준다.
          (없는 영상 재생 시도 → 404 'denied' 오류가 뜨던 것 대체) */
       <div className="w-full flex flex-col items-center p-2">
        <img src={`/mofile/thumbnail/${file.thumbnail_path}`} alt={file.title} className="w-full max-h-96 object-contain" />
        <div className="text-xs text-gray-500 mt-2">
         {(currentStatus as string) === 'deferred' ? '승인하면 재생용 영상으로 준비됩니다 · 포스터 미리보기' : '게재 준비 중 · 포스터 미리보기'}
        </div>
       </div>
      ) : (
       <div className="w-full h-64 bg-blue-50 border border-blue-200 flex flex-col items-center justify-center p-6">
        <svg className="w-12 h-12 text-blue-500 mb-3 animate-spin" fill="none" viewBox="0 0 24 24">
         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
        </svg>
        <div className="text-blue-700 font-semibold">
         {transcodeBadge({
          server: {
           status: currentStatus,
           progress: file.transcode_progress,
           preparing: currentStatus === 'processing' && !(file as any).encoding_started_at,
          },
          entry: mainEntry,
         }).text || '게재 준비 중'}
        </div>
        {(mainEntry?.progress ?? 0) > 0 && (
         <div className="w-64 mt-3">
          <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
           <div
            className="h-full bg-blue-500 transition-all"
            style={{ width: `${Math.min(100, Math.max(0, mainEntry?.progress ?? 0))}%` }}
           />
          </div>
          <div className="text-xs text-blue-700 mt-1 text-center">
           {Math.floor(mainEntry?.progress ?? 0)}%
           {mainEntry?.etaSec != null && mainEntry.etaSec > 0 && (
            <span className="ml-2">· 약 {Math.ceil(mainEntry.etaSec)}초 남음</span>
           )}
          </div>
         </div>
        )}
       </div>
      )
     ) : isImage ? (
      <img
       src={file.file_url}
       alt={file.title}
       className="w-full max-h-96 object-contain"
      />
     ) : (
      <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
       <svg className="w-24 h-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
         d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
       </svg>
      </div>
     )}
    </div>

    <div className="space-y-3 text-sm">
     <div className="flex justify-between">
      <span className="text-gray-600">파일 종류:</span>
      <span>{file.file_type || '알 수 없음'}</span>
     </div>
     {mediaInfo?.codec && (
      <div className="flex justify-between">
       <span className="text-gray-600">코덱:</span>
       <span className="uppercase">
        {mediaInfo.codec}
        {mediaInfo.resolution && <span className="ml-2 text-gray-500 normal-case">{mediaInfo.resolution}</span>}
        {mediaInfo.fps != null && <span className="ml-1 text-gray-500 normal-case">{mediaInfo.fps}fps</span>}
       </span>
      </div>
     )}
     {durationLabel && (
      <div className="flex justify-between">
       <span className="text-gray-600">재생 시간:</span>
       <span>{durationLabel}</span>
      </div>
     )}
     {/* 달력에서 연 파일은 /info 로 채워지는데 그 응답에 업로드 일자가 없다. 값이 없으면
         행 자체를 숨긴다 — 빈 문자열을 그대로 넘기면 화면에 "Invalid Date" 가 찍힌다. */}
     {file.created_at && Number.isFinite(new Date(file.created_at).getTime()) && (
      <div className="flex justify-between">
       <span className="text-gray-600">업로드 일자:</span>
       <span>{formatDateTime(file.created_at)}</span>
      </div>
     )}
     {file.description && (
      <div>
       <span className="text-gray-600">설명:</span>
       <p className="mt-1">{file.description}</p>
      </div>
     )}
    </div>

    {/* 해상도 변형 섹션 (영상 파일에만). readOnly(남의 영상)에선 섹션 자체를 감춘다 —
        목록 조회부터 소유자 게이트(403)라 '로딩 중'에 영원히 머무는 것을 방지. */}
    {isVideo && !readOnly && (
     <div className="mt-6 border-t pt-4">
      <div className="flex items-center justify-between mb-3">
       <h3 className="text-lg font-semibold">화면 맞춤본</h3>
       {/* 변형 추가도 변경 수단 — readOnly(남의 영상)에서는 감춘다. */}
       {!readOnly && <div>
        <input
         ref={variantFileInputRef}
         type="file"
         accept="video/*"
         className="hidden"
         onChange={handleVariantUpload}
        />
        <button
         onClick={() => variantFileInputRef.current?.click()}
         disabled={isUploadingVariant}
         className="px-3 py-1 bg-blue-500 text-white text-sm hover:bg-blue-600 disabled:opacity-50"
        >
         {isUploadingVariant ? `올리는 중 ${Math.round(variantUploadPct)}%` : '+ 화면 맞춤본 올리기'}
        </button>
       </div>}
      </div>
      {variantError && (
       <div className="mb-2 text-sm text-red-600 bg-red-50 px-3 py-2 border border-red-200">
        {variantError}
       </div>
      )}
      {variantNotice && (
       <div className="mb-2 text-sm text-green-700 bg-green-50 px-3 py-2 border border-green-200">
        {variantNotice}
       </div>
      )}
      <table className="w-full text-sm">
       <thead>
        <tr className="text-left text-gray-500 border-b">
         <th className="py-1 pr-2">해상도</th>
         <th className="py-1 pr-2">용량</th>
         <th className="py-1 pr-2">상태</th>
         <th className="py-1"></th>
        </tr>
       </thead>
       <tbody>
        {variants.length === 0 && (
         <tr><td colSpan={4} className="py-2 text-gray-400 italic">목록 로딩 중...</td></tr>
        )}
        {variants.map(v => {
         // 변형의 경우 WS push 진행률을 우선 사용. 회전본은 variantKey(rotation 포함)로 키잉해
         // 같은 해상도의 회전/미회전 변형이 상태 store 를 공유해 깜빡이지 않게 한다.
         const wsEntry = v.kind === 'variant' && v.resolution
          ? variantStateMap.get(`${file.file_hash}::${variantSlug(v.resolution, v.rotation, v.fps)}`)
          : null;
         const status = wsEntry?.status ?? v.transcode_status;
         const progress = wsEntry?.progress ?? v.transcode_progress ?? 0;

         // 재생 가능: done 상태만. 클릭 시 상단 video 요소가 이 변형 URL로 전환.
         const playable = status === 'done';
         // 같은 해상도라도 회전본(r0/r90)·fps(30/60)는 별개 아티팩트 → resolution+rotation+fps
         // 모두 비교. (셋 중 하나라도 빠뜨리면 같은 해상도 변형들이 함께 하이라이트되는 버그.)
         const isSelected = v.kind === 'main'
          ? selectedVariant === null
          : selectedVariant?.resolution === v.resolution
            && (selectedVariant?.rotation ?? 0) === (v.rotation ?? 0)
            && canonFps(selectedVariant?.fps) === canonFps(v.fps);
         const handleRowClick = () => {
          if (!playable) return;
          // main 행은 selectedVariant=null로 (file.file_url 사용)
          setSelectedVariant(v.kind === 'main' ? null : v);
         };

         return (
          <tr
           key={`${v.kind}-${v.resolution}-${v.rotation ?? 0}-${canonFps(v.fps)}`}
           onClick={handleRowClick}
           className={`border-b border-gray-50 ${
            playable ? 'cursor-pointer hover:bg-blue-50' : 'cursor-default'
           } ${isSelected ? 'bg-blue-100' : ''}`}
           title={playable ? '클릭하면 위 화면에서 재생' : ''}
          >
           <td className="py-1.5 pr-2 font-mono text-xs">
            <span className="mr-1">{aspectIcon(v.width, v.height)}</span>
            {v.resolution || '-'}
            {v.fps != null && <span className="ml-1 text-[11px] text-gray-500">{canonFps(v.fps)}fps</span>}
            {rotationLabel(v.rotation) && <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-purple-100 text-purple-800">{rotationLabel(v.rotation)}</span>}
            {v.kind === 'main' && <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-gray-200 text-gray-700">대표</span>}
            {isSelected && <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-blue-500 text-white">▶ 재생 중</span>}
           </td>
           <td className="py-1.5 pr-2 text-gray-600">{v.file_size ? formatBytes(v.file_size) : '-'}</td>
           <td className="py-1.5 pr-2">
            {status === 'done' && <span className="text-green-700">준비됨</span>}
            {status === 'failed' && <span className="text-red-700">실패</span>}
            {(status === 'pending' || status === 'processing') && (
             <span className="text-blue-700">
              {status === 'pending' ? '기다리는 중' : `준비 중${progress > 0 ? ` ${Math.floor(progress)}%` : ''}`}
             </span>
            )}
           </td>
           <td className="py-1.5 text-right">
            {!readOnly && v.kind === 'variant' && v.resolution && (
             <button
              onClick={(e) => { e.stopPropagation(); handleVariantDelete(variantSlug(v.resolution!, v.rotation, v.fps)); }}
              className="text-xs text-red-600 hover:text-red-800"
             >
              삭제
             </button>
            )}
           </td>
          </tr>
         );
        })}
       </tbody>
      </table>
      {/* 추가 변형 생성 — 업로드된 main 파일을 source 로 새 해상도 트랜스코딩.
          변경 수단이므로 readOnly(남의 영상)에서는 감춘다. */}
      {!readOnly && <div className="mt-3">
       <button
        onClick={async () => {
         const next = !showVariantGen;
         setShowVariantGen(next);
         if (next && recommendedVariants.length === 0 && file) {
          try {
           const resp = await fetch('/mofile/recommended-variants', { credentials: 'include' });
           const body = await resp.json();
           if (body?.success && Array.isArray(body.data?.variants)) {
            setRecommendedVariants(body.data.variants);
           }
          } catch {/* noop */}
         }
        }}
        className="text-xs text-blue-600 hover:text-blue-800 underline"
       >
        {showVariantGen ? '▼ 화면 맞춤본 만들기 닫기' : '+ 화면 맞춤본 만들기'}
       </button>
       {showVariantGen && (
        <div className="mt-2 p-3 bg-gray-50 border border-gray-200">
         <p className="text-xs text-gray-600 mb-2">
          대표 영상으로 다른 화면 크기에 맞는 맞춤본을 만듭니다. 최근 연결된 화면들의 크기가 추천 목록입니다.
         </p>
         {recommendedVariants.length === 0 && (
          <p className="text-xs text-gray-400 italic">추천 해상도가 없습니다 (24시간 내 보고된 MoClient 가 없음).</p>
         )}
         {recommendedVariants.map(rv => {
          const exists = variants.some(v => v.kind === 'variant' && v.resolution === rv.resolution);
          const selected = selectedGenVariants.has(rv.resolution);
          return (
           <label key={rv.resolution} className={`flex items-center gap-2 py-1 text-xs ${exists ? 'opacity-50' : 'cursor-pointer'}`}>
            <input
             type="checkbox"
             disabled={exists}
             checked={selected}
             onChange={(e) => {
              const next = new Set(selectedGenVariants);
              if (e.target.checked) next.add(rv.resolution);
              else next.delete(rv.resolution);
              setSelectedGenVariants(next);
             }}
            />
            <span className="font-mono">{rv.resolution}</span>
            {rv.fps && <span className="text-gray-500">@ {rv.fps}fps</span>}
            <span className="text-gray-400">— {rv.clientNames?.slice(0, 3).join(', ')}{rv.clientNames?.length > 3 ? ' 외' : ''}</span>
            {exists && <span className="text-green-700 ml-auto">이미 있음</span>}
           </label>
          );
         })}
         <div className="mt-3 flex justify-end gap-2">
          <button
           disabled={isGeneratingVariants || selectedGenVariants.size === 0}
           onClick={async () => {
            if (!file) return;
            setIsGeneratingVariants(true);
            try {
             const payload = recommendedVariants
              .filter(rv => selectedGenVariants.has(rv.resolution))
              .map(rv => ({ width: rv.width, height: rv.height, fps: rv.fps ?? undefined }));
             const resp = await fetch(`/mofile/${file.file_hash}/variants/transcode`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ variants: payload }),
             });
             const body = await resp.json();
             if (!resp.ok || !body?.success) {
              setVariantError(body?.error || '화면 맞춤본 만들기 실패');
              return;
             }
             const { enqueued, skipped } = body.data || {};
             setVariantError(null);
             setVariantNotice(`화면 맞춤본 ${enqueued?.length ?? 0}개를 준비하기 시작했습니다${skipped?.length ? ` (이미 있는 ${skipped.length}개는 건너뜀)` : ''}.`);
             setSelectedGenVariants(new Set());
             await loadVariants();
            } finally {
             setIsGeneratingVariants(false);
            }
           }}
           className="px-3 py-1 text-xs bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
          >
           {isGeneratingVariants ? '준비 시작 중...' : `선택한 ${selectedGenVariants.size}개 만들기`}
          </button>
         </div>
        </div>
       )}
      </div>}
     </div>
    )}

    {/* 송출 통계 — 원장(play_logs, 3개월 보존) 기준. "송출" = 재생 완료(정산과 같은 사영). */}
    {!readOnly && (
     <div className="mt-6 border-t border-gray-200 pt-3">
      <button
       type="button"
       className="text-sm font-medium text-gray-700 hover:text-gray-900"
       onClick={async () => {
        const opening = !statsOpen;
        setStatsOpen(opening);
        if (opening && !stats && !statsLoading) {
         setStatsLoading(true);
         try {
          const r = await fetch(`/mofile/${file.file_hash}/stats`, { credentials: 'include' });
          const j = await r.json();
          if (j.success) setStats(j.data);
         } catch { /* 펼침 유지 — 아래에서 로드 실패 표기 */ }
         setStatsLoading(false);
        }
       }}
      >
       {statsOpen ? '▼' : '▶'} 송출 통계
      </button>
      {statsOpen && (
       <div className="mt-2 text-sm">
        {statsLoading ? (
         <div className="text-gray-500">불러오는 중...</div>
        ) : !stats ? (
         <div className="text-gray-500">통계를 불러오지 못했습니다.</div>
        ) : (
         <div className="space-y-3">
          <div className="flex gap-4">
           <span>총 <b>{stats.total.toLocaleString()}</b>회 송출</span>
           <span className="text-gray-500">최근 30일 {stats.recent30.toLocaleString()}회</span>
          </div>
          {stats.daily.length > 0 && (
           <div className="flex items-end gap-[2px] h-12" title="최근 30일 일별 송출">
            {stats.daily.map(d => {
             const max = Math.max(...stats.daily.map(x => x.plays), 1);
             return (
              <div key={d.day} className="flex-1 bg-blue-400 min-w-[3px]"
               style={{ height: `${Math.max(8, (d.plays / max) * 100)}%` }}
               title={`${d.day}: ${d.plays}회`} />
             );
            })}
           </div>
          )}
          {stats.byFamily.length === 0 ? (
           <div className="text-gray-500">아직 송출 기록이 없습니다.</div>
          ) : (
           <table className="w-full text-xs">
            <thead>
             <tr className="text-left text-gray-500 border-b border-gray-200">
              <th className="py-1 font-normal">화면</th>
              <th className="py-1 font-normal text-right">송출</th>
              <th className="py-1 font-normal text-right">마지막 송출</th>
             </tr>
            </thead>
            <tbody>
             {stats.byFamily.map((r, i) => (
              <tr key={r.familyHash ?? i} className="border-b border-gray-100">
               <td className="py-1">
                {r.familyName}
                {r.isMine
                 ? <span className="ml-1.5 text-[10px] px-1 py-px bg-gray-100 text-gray-500">내 화면</span>
                 : <span className="ml-1.5 text-[10px] px-1 py-px bg-purple-100 text-purple-600">제휴</span>}
               </td>
               <td className="py-1 text-right font-medium">{r.plays.toLocaleString()}</td>
               <td className="py-1 text-right text-gray-500">
                {r.lastAt ? new Date(r.lastAt).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
               </td>
              </tr>
             ))}
            </tbody>
           </table>
          )}
          <div className="text-[11px] text-gray-400">
           재생이 끝까지 완료된 횟수만 셉니다 · 기록은 최근 3개월 보존분 기준
          </div>
         </div>
        )}
       </div>
      )}
     </div>
    )}

    <div className="flex justify-end gap-3 mt-6">
     {/* 내려받기 — 재생 URL 과 같은 엔드포인트에 ?attachment=1 만 붙인다.
         서버가 Content-Disposition 을 attachment 로 바꾸고, 트랜스코딩이 끝난 파일은
         확장자를 .mp4 로 맞춘다(원본이 .mov 여도 내용은 mp4 라서).
         <a download> 이 아니라 서버 헤더로 처리하는 이유: download 속성은 파일명을
         클라가 정하는데, 실제 확장자·인코딩을 아는 쪽은 서버다. */}
     <a
      href={`/mofile/download/${file.file_hash}?attachment=1`}
      className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600"
     >
      내려받기
     </a>
     {/* 노출 종료 — 삭제와 다르다: 파일은 남고 노출만 끝난다(재행사 대비). QUICKUI.md */}
     {!readOnly && <button
      onClick={async () => {
       if (!confirm('이 영상의 노출을 종료할까요?\n\n· 모든 화면(다른 운영처 포함)에서 다음 동기화 때 사라집니다\n· 파일은 보관되어 나중에 다시 게시할 수 있습니다')) return;
       try {
        const r = await fetch(`/mofile/${file.file_hash}/cancel-exposure`, { method: 'POST', credentials: 'include' });
        const j = await r.json();
        if (j.success) {
         alert(j.data?.message || '노출이 종료되었습니다.');
         onClose();
        } else {
         alert(j.error || '노출 종료에 실패했습니다.');
        }
       } catch {
        alert('노출 종료에 실패했습니다.');
       }
      }}
      className="px-4 py-2 bg-amber-500 text-white hover:bg-amber-600"
      title="파일은 남기고 모든 화면에서의 노출만 끝냅니다. 다시 게시할 수 있습니다."
     >
      노출 종료
     </button>}
     {!readOnly && <button
      onClick={handleDelete}
      className="px-4 py-2 bg-red-500 text-white hover:bg-red-600"
     >
      삭제
     </button>}
     {readOnly && (
      <span className="mr-auto self-center text-xs text-gray-400">
       다른 운영처가 올린 영상입니다 — 보기 전용
      </span>
     )}
     <button
      onClick={onClose}
      className="px-4 py-2 bg-gray-300 text-gray-700 hover:bg-gray-400"
     >
      닫기
     </button>
    </div>
   </div>
  </div>
 );
};
